import { escape } from 'lodash';
import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { Alert, Modal, Button } from 'react-bootstrap';
import { graphql } from 'react-apollo';
import ReactSelect from 'react-select';
import AsyncCreatable from 'react-select/lib/AsyncCreatable';
import {
  CREATE_NCR_MUTATION,
  NCRCOMPANYLIST_LOOKUP_QUERY,
  NCRPROJECTLIST_LOOKUP_QUERY,
  NCRJOBLIST_LOOKUP_QUERY,
  NCRSUPPLIERS_LOOKUP_QUERY,
  NCRCHAMPION_LOOKUP_QUERY,
  NCRINITIATOR_LOOKUP_QUERY,
  NCRSOURCES_LOOKUP_QUERY,
  NCRREASONS_LOOKUP_QUERY,
  NCRLOCATIONS_LOOKUP_QUERY,
  NCRAREAS_LOOKUP_QUERY
} from '../../../timesheet/queries';
import { fetchFusionManagePicklist, fetchFusionManagePicklistDirect, mergePicklists } from './fusionManageUtils';
import { mergeSupplierLists } from './supplierUtils';
import {
  extractUniqueGlobalSites,
  extractUniqueSites,
  extractUniqueDepartments,
  parseLocationString,
  deriveGlobalSiteFromSite
} from './locationUtils';
import { useFormSubmission } from '../../hooks/useFormSubmission';
import { WORKSPACE_IDS } from '../../constants/fusionManageConstants';
import InfoTooltip from '../InfoTooltip';

// import '../../../../userCodes.js';

// Debounce hook for optimizing filtered option fetches
const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};

function CustomerComplaintForm({ onFormSubmitSuccess, onFormSubmitError }) {
  const initialState = {
    // Auto-generated fields
    "Number": "", // auto generated

    // Required fields regardless of Project or Technology NCR
    "ProjectOrTechnology": null, // dropdown search - null for no selection
    "Market": null, // dropdown - picklist from Fusion Manage
    "NCRTitle": "", // input
    "Description": "", // input box
    "IncidentDate": "", // date input
    "InternalOrExternal": null, // dropdown search - null for no selection
    "RelatedSupplier": null, // pulled from epicor dropdown search - null for no selection
    "CostOfNCR": "", // input box

    // Required for Project NCR only
    "ProjectID": null, // pulled from epicor dropdown search - null for no selection
    "ProjectName": null, // pulled from epicor dropdown search - null for no selection
    "JobNumber": null, // pulled from epicor dropdown search - null for no selection
    "DrawingNumber": "", // input

    // Required for Technology NCR only
    "ProductName": null, // pulled from epicor dropdown search - null for no selection
    "ProductVariant": "", // text input field
    "SoftwareVersion": "", // input (labeled as Software Variant)
    "ProductSerialNumber": "", // input
    "AffectedQuantity": "", // input

    // Required fields regardless of Project or Technology NCR
    "GlobalSiteAssigned": null, // dropdown search - null for no selection
    "SiteAssigned": null, // dropdown search - null for no selection
    "DepartmentAssigned": null, // dropdown search - null for no selection
    "NCRChampionAssigned": null, // dropdown - null for no selection
    "NCRCapabilityAssigned": null, // dropdown - null for no selection
    "QAEmail": null, // hidden field - not shown in form but submitted
    "GlobalSiteRaisedBy": null, // dropdown search - null for no selection
    "SiteRaisedBy": null, // dropdown search - null for no selection
    "DepartmentRaisedBy": null, // dropdown search - null for no selection
    "RaisedBy": "", // input
    "RaisedDate": "", // date input

    // Legacy fields for backward compatibility
    "JobCode": "",
    "SourceTypeID": null, // dropdown - null for no selection
    "SourceType": "",
    "CaseTitle": "",
    "CaseSummary": "",
    "SupplierNumber": "",
    "SupplierName": "",
    "IncidentLocation": null, // dropdown - null for no selection
    "OtherLocation": "",
    "IncidentArea": null, // dropdown - null for no selection
    "NCRChampionID": null, // dropdown - null for no selection
    "NCRChampion": "",
    "CaseTypeID": null, // dropdown - null for no selection
    "CaseType": "",
    "ProjectRelated": true,
    "SafetyCritical": false,
    "LegalCompliance": false,
    "NCRReason": null, // dropdown - null for no selection
    "NCRInitiator": null, // dropdown - null for no selection
    "Notes": "",
    "NCRReasonID": null, // dropdown - null for no selection
    "NCRInitiatorID": null // dropdown - null for no selection
  };

  const [formData, setFormData] = useState(initialState);
  const [projects, setProjects] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [locations, setLocations] = useState([]);
  const [champions, setChampions] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [initiator, setInitiator] = useState([]);
  const [reasons, setReasons] = useState([]);
  const [sources, setSources] = useState([]);
  const [areas, setAreas] = useState([]);
  
  // New state variables for enhanced form
  const [productNames, setProductNames] = useState([]);
  const [productVariants, setProductVariants] = useState([]);
  const [globalSites, setGlobalSites] = useState([]);
  const [sites, setSites] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [ncrChampions, setNcrChampions] = useState([]);
  const [raisedByUsers, setRaisedByUsers] = useState([]);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [fusionManageStatus, setFusionManageStatus] = useState(null);
  const [fusionManageMessage, setFusionManageMessage] = useState('');
  const alertRef = useRef(null);

  const [dropdownData, setDropdownData] = useState([]);
  
  // Fusion Manage picklist state
  const [fusionProjectOrTechnology, setFusionProjectOrTechnology] = useState([]);
  const [fusionMarket, setFusionMarket] = useState([]);
  const [fusionInternalOrExternal, setFusionInternalOrExternal] = useState([]);
  const [fusionLocations, setFusionLocations] = useState([]);
  const [fusionNCRChampions, setFusionNCRChampions] = useState([]);
  const [fusionDepartments, setFusionDepartments] = useState([]);
  
  // Parsed location state
  const [parsedLocations, setParsedLocations] = useState({
    globalSites: [],
    sites: [],
    departments: []
  });

  // Filtered options from Fusion Manage (based on parent selections)
  const [filteredSiteOptionsAssigned, setFilteredSiteOptionsAssigned] = useState([]);
  const [filteredDepartmentOptionsAssigned, setFilteredDepartmentOptionsAssigned] = useState([]);
  const [filteredChampionOptionsAssigned, setFilteredChampionOptionsAssigned] = useState([]);
  const [filteredCapabilityOptionsAssigned, setFilteredCapabilityOptionsAssigned] = useState([]);
  const [filteredQAEmailOptionsAssigned, setFilteredQAEmailOptionsAssigned] = useState([]);
  const [filteredSiteOptionsRaisedBy, setFilteredSiteOptionsRaisedBy] = useState([]);
  const [filteredDepartmentOptionsRaisedBy, setFilteredDepartmentOptionsRaisedBy] = useState([]);
  const [loadingFilteredOptions, setLoadingFilteredOptions] = useState(false);
  const [validGlobalSiteOptionsAssigned, setValidGlobalSiteOptionsAssigned] = useState([]);
  const [validGlobalSiteOptionsRaisedBy, setValidGlobalSiteOptionsRaisedBy] = useState([]);
  const [loadingGlobalSiteOptions, setLoadingGlobalSiteOptions] = useState(false);

  // Loading states for dropdown data
  const [loadingFusionData, setLoadingFusionData] = useState(false);
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [loadingJobs, setLoadingJobs] = useState(false);
  const [loadingChampions, setLoadingChampions] = useState(false);
  const mergeSupplierData = useCallback(
    (incomingSuppliers = [], {preferIncoming = false, sourceLabel = 'unknown'} = {}) => {
      if (!Array.isArray(incomingSuppliers) || incomingSuppliers.length === 0) {
        console.log(
          `[CustomerComplaintForm] mergeSupplierData skipped for ${sourceLabel} - no suppliers provided`
        );
        return;
      }

      setSuppliers(prevSuppliers => {
        const merged = mergeSupplierLists(prevSuppliers, incomingSuppliers, {preferIncoming});
        console.log(
          `[CustomerComplaintForm] mergeSupplierData (${sourceLabel}) -> incoming=${incomingSuppliers.length}, total=${merged.length}, preferIncoming=${preferIncoming}`
        );
        return merged;
      });
    },
    []
  );

  // hasInteracted and isSubmitting are now provided by useFormSubmission hook
  const handleChange = (event) => {
    const { name, value, type, checked } = event.target;
  setHasInteracted(true);
    // ALWAYS use functional setState to prevent race conditions and lost updates
    setFormData(prevFormData => ({
      ...prevFormData,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  // Parse Project ID from label: "25W0159 - Project Name" or "25W0159-Project Name" -> "25W0159"
  // Extracts everything BEFORE the dash and removes leading/trailing whitespace
  const parseProjectIdFromLabel = (label) => {
    if (!label) return '';
    const trimmed = String(label).trim();
    // Find the first dash (prefer " - " with spaces, then just "-")
    const dashIndex = trimmed.indexOf(' - ') !== -1 ? trimmed.indexOf(' - ') : trimmed.indexOf('-');
    if (dashIndex === -1) {
      // No dash found, return everything before first space or the whole string if no space
      const firstSpaceIndex = trimmed.indexOf(' ');
      const result = firstSpaceIndex === -1 ? trimmed : trimmed.substring(0, firstSpaceIndex);
      return result.trim(); // Remove leading/trailing whitespace
    }
    // Extract everything before the dash (handles alphanumeric IDs like "25W0159")
    const beforeDash = trimmed.substring(0, dashIndex);
    return beforeDash.trim(); // Remove leading/trailing whitespace
  };

  // Parse Project Name from label: "25W0159 - Project Name" or "25W0159-Project Name" -> "Project Name"
  // Extracts everything AFTER the dash and removes leading/trailing whitespace
  const parseProjectNameFromLabel = (label) => {
    if (!label) return '';
    const trimmed = String(label).trim();
    // Try " - " first, then just "-"
    let dashIndex = trimmed.indexOf(' - ');
    let skipLength = 3; // Skip " - "
    if (dashIndex === -1) {
      dashIndex = trimmed.indexOf('-');
      skipLength = 1; // Skip "-"
    }
    if (dashIndex === -1) return '';
    // Extract everything after the dash
    const afterDash = trimmed.substring(dashIndex + skipLength);
    return afterDash.trim(); // Remove leading/trailing whitespace
  };

  const handleSelectChange = (selectedOption, field) => {
    console.log(`[handleSelectChange] Field: ${field}, Value:`, selectedOption);
    setHasInteracted(true);

    // ALWAYS use prevFormData callback to avoid race conditions and lost updates
    setFormData(prevFormData => {
      if (field === 'RelatedSupplier') {
        // Single select handling - store the selected option object or null
        console.log('[handleSelectChange] RelatedSupplier - selectedOption:', selectedOption);
        
        if (selectedOption === null || selectedOption === undefined) {
          // Cleared or no selection
          console.log('[handleSelectChange] RelatedSupplier - cleared/null');
          return {
            ...prevFormData,
            [field]: null,
          };
        } else {
          // Store the selected option object (contains value and label)
          console.log('[handleSelectChange] RelatedSupplier - storing selected option');
          return {
            ...prevFormData,
            [field]: selectedOption,
          };
        }
      }
      
      if (field === 'ProjectID') {
        // Parse ProjectID and ProjectName from the label (e.g., "25W0159 - Jelly Roll UK EU 2025")
        const label = selectedOption?.label || '';
        const projectId = parseProjectIdFromLabel(label); // Extract "25W0159" from label
        const projectName = parseProjectNameFromLabel(label); // Extract "Jelly Roll UK EU 2025" from label
        setJobs([]);
        return {
          ...prevFormData,
          ProjectID: projectId, // Store parsed project ID (e.g., "25W0159")
          ProjectName: projectName, // Store parsed project name (e.g., "Jelly Roll UK EU 2025")
          JobNumber: null,
          JobCode: ''
        };
      }

      if (field === 'ProjectName') {
        // Use the option's value directly (it's already the project ID as a string)
        const projectId = selectedOption?.value || '';
        const label = selectedOption?.label || '';
        const projectName = parseProjectNameFromLabel(label);
        setJobs([]);
        return {
          ...prevFormData,
          ProjectID: projectId, // Store the value directly from the option
          ProjectName: projectName,
          JobNumber: null,
          JobCode: ''
        };
      } else if (field === 'NCRChampionAssigned') {
        // Extract QAEmail from the champion object when champion is selected
        const qaEmail = selectedOption?.email 
          || selectedOption?.QA_EMAIL 
          || selectedOption?.qa_email
          || selectedOption?.rawItem?.QA_EMAIL
          || selectedOption?.rawItem?.qa_email
          || null;
        
        console.log(`[handleSelectChange] NCRChampionAssigned selected. Extracted QAEmail:`, qaEmail);
        
        return {
          ...prevFormData,
          [field]: selectedOption,
          QAEmail: qaEmail
        };
      } else if (field === 'ProjectOrTechnology') {
        // When ProjectOrTechnology changes, clear fields that are no longer relevant
        const newType = selectedOption?.value || selectedOption?.label || selectedOption;
        const newFormData = {
          ...prevFormData,
          [field]: selectedOption,
        };
        
        // If switching TO "Project": Clear Product-related fields (but keep products loaded for when user switches back)
        if (newType === 'Project') {
          newFormData.ProductName = null;
          newFormData.ProductVariant = ''; // Changed from null to '' since it's now a text input
          newFormData.SoftwareVersion = '';
          newFormData.ProductSerialNumber = '';
          newFormData.AffectedQuantity = '';
          // DON'T clear productNames - keep them loaded like IPIForm does with fusionProductTypes
          // setProductNames([]);
        }
        // If switching TO "Product": Clear Project-related fields
        else if (newType === 'Product') {
          newFormData.ProjectID = null;
          newFormData.ProjectName = null;
          newFormData.JobNumber = null;
          newFormData.JobCode = '';
          newFormData.DrawingNumber = '';
          setProjects([]);
          setJobs([]);
          // Products should already be loaded from loadInitialData
        }
        // If switching to something else (e.g., "Compliance"): Clear both Project and Product fields (but keep products loaded)
        else {
          newFormData.ProjectID = null;
          newFormData.ProjectName = null;
          newFormData.JobNumber = null;
          newFormData.JobCode = '';
          newFormData.DrawingNumber = '';
          newFormData.ProductName = null;
          newFormData.ProductVariant = ''; // Changed from null to '' since it's now a text input
          newFormData.SoftwareVersion = '';
          newFormData.ProductSerialNumber = '';
          newFormData.AffectedQuantity = '';
          setProjects([]);
          setJobs([]);
          // DON'T clear productNames - keep them loaded like IPIForm does with fusionProductTypes
          // setProductNames([]);
        }
        
        return newFormData;
      } else if (field === 'ProductName') {
        // Store ProductName with the extracted text (label) as the value
        // The option already has the extracted text after the dash in both value and label
        const productNameValue = selectedOption?.label || selectedOption?.value || selectedOption;
        return {
          ...prevFormData,
          ProductName: {
            value: productNameValue,
            label: productNameValue
          }
        };
      } else {
        const newFormData = {
          ...prevFormData,
          [field]: selectedOption,
        };
        
        // Cascading filters: clear dependent fields when parent changes
        if (field === 'GlobalSiteAssigned') {
          // Clear Site, Department, Champion, Capability, and QA Email when Global Site changes
          newFormData.SiteAssigned = null;
          newFormData.DepartmentAssigned = null;
          newFormData.NCRChampionAssigned = null;
          newFormData.NCRCapabilityAssigned = null;
          newFormData.QAEmail = null;
          // Clear filtered options (will be refetched by useEffect)
          setFilteredSiteOptionsAssigned([]);
          setFilteredDepartmentOptionsAssigned([]);
          setFilteredChampionOptionsAssigned([]);
          setFilteredCapabilityOptionsAssigned([]);
          setFilteredQAEmailOptionsAssigned([]);
        } else if (field === 'SiteAssigned') {
          // Clear Department, Champion, Capability, and QA Email when Site changes
          newFormData.DepartmentAssigned = null;
          newFormData.NCRChampionAssigned = null;
          newFormData.NCRCapabilityAssigned = null;
          newFormData.QAEmail = null;
          // Clear filtered options
          setFilteredDepartmentOptionsAssigned([]);
          setFilteredChampionOptionsAssigned([]);
          setFilteredCapabilityOptionsAssigned([]);
          setFilteredQAEmailOptionsAssigned([]);
        } else if (field === 'DepartmentAssigned') {
          // Clear Champion, Capability, and QA Email when Department changes
          newFormData.NCRChampionAssigned = null;
          newFormData.NCRCapabilityAssigned = null;
          newFormData.QAEmail = null;
          // Clear filtered options
          setFilteredChampionOptionsAssigned([]);
          setFilteredCapabilityOptionsAssigned([]);
          setFilteredQAEmailOptionsAssigned([]);
        } else if (field === 'GlobalSiteRaisedBy') {
          // Clear Site and Department when Global Site changes
          newFormData.SiteRaisedBy = null;
          newFormData.DepartmentRaisedBy = null;
          // Clear filtered options
          setFilteredSiteOptionsRaisedBy([]);
          setFilteredDepartmentOptionsRaisedBy([]);
        } else if (field === 'SiteRaisedBy') {
          // Clear Department when Site changes
          newFormData.DepartmentRaisedBy = null;
          // Clear filtered options
          setFilteredDepartmentOptionsRaisedBy([]);
        }
        
        console.log(`[handleSelectChange] Updated ${field} in formData. New value:`, newFormData[field]);
        return newFormData;
      }
    });
  };
  

  // Helper function to set RaisedDate on mount
  useEffect(() => {
    setFormData(prevData => ({
      ...prevData,
      RaisedDate: new Date().toISOString().split('T')[0]
    }));
  }, []);

  // Helper function to safely fetch GraphQL data (moved outside useEffect for reuse)
  const fetchGraphQL = useCallback(async (query, variables = {}) => {
    const queryString = (query && query.loc) ? query.loc.source.body : query;
    try {
      const response = await fetch('/api/graphql', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ query: queryString, variables }),
      });
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      const data = await response.json();
      return data.data;
    } catch (error) {
      console.error(`[CustomerComplaintForm] GraphQL fetch error:`, error);
      return null;
    }
  }, []);

  useEffect(() => {
    let cancelled = false;

    // Batch all initial data fetches in parallel
    const loadInitialData = async () => {
      const projectIdValue = formData.ProjectID?.value || formData.ProjectID;

      console.log('[CustomerComplaintForm] loadInitialData triggered - ProjectID:', projectIdValue);

      // Fetch Fusion Manage data in parallel
      const parsedLocationsPromise = fetch('/api/fm/locations/parsed', {
        credentials: 'include'
      })
        .then(res => res.ok ? res.json() : { success: false })
        .then(data => data.success ? data : { globalSites: [], sites: [], departments: [] })
        .catch(() => ({ globalSites: [], sites: [], departments: [] }));

      setLoadingFusionData(true);
      const fusionPicklistsPromise = Promise.all([
        fetchFusionManagePicklistDirect('CUSTOM_LOOKUP_DEFECT_TYPE'), // Workspace 736 uses DEFECT_TYPE instead of TAIT_NCR_TYPE
        fetchFusionManagePicklistDirect('CUSTOM_LOOKUP_QA_MARKET'),
        fetchFusionManagePicklistDirect('CUSTOM_LOOKUP_INTERNAL_VS_EXTERNAL'),
        fetchFusionManagePicklistDirect('CUSTOM_LOOKUP_WS_QA_LOCATION'),
        fetchFusionManagePicklistDirect('CUSTOM_LOOKUP_WS_QA_NCR_CHAMPIONS'),
        fetchFusionManagePicklistDirect('CUSTOM_LOOKUP_WS_SUPPLIERS'),
        fetchFusionManagePicklistDirect('CUSTOM_LOOKUP_WS_QA_PRODUCT_TYPE'), // Load products upfront
        fetch('/api/fm/departments', { credentials: 'include' })
          .then(res => res.json())
          .then(data => data.success ? data.options : [])
          .catch(() => [])
      ]).catch(() => [[], [], [], [], [], [], [], []]);

      // Execute independent fetches in parallel
      const [parsedLocationsData, fusionPicklists] = await Promise.all([
        parsedLocationsPromise,
        fusionPicklistsPromise
      ]);

      if (cancelled) return;

      // Set Fusion Manage data immediately
      if (parsedLocationsData.globalSites || parsedLocationsData.sites || parsedLocationsData.departments) {
        setParsedLocations({
          globalSites: parsedLocationsData.globalSites || [],
          sites: parsedLocationsData.sites || [],
          departments: parsedLocationsData.departments || []
        });
      }

      const [projectOrTech, market, internalOrExt, locations, champions, fusionSuppliers, products, departments] = fusionPicklists;
      setFusionProjectOrTechnology(projectOrTech);
      setFusionMarket(market);
      setFusionInternalOrExternal(internalOrExt);
      setFusionLocations(locations);
      setFusionNCRChampions(champions);
      setFusionDepartments(departments);
      // Store products for use when "Product" is selected
      if (products && products.length > 0) {
        console.log('[CustomerComplaintForm] Setting productNames from loadInitialData:', products.length, 'products');
        console.log('[CustomerComplaintForm] Sample product:', products[0]);
        console.log('[CustomerComplaintForm] All products received:', products);
        console.log('[CustomerComplaintForm] Product structure check:', products.map(p => ({
          hasValue: !!p.value,
          hasLabel: !!p.label,
          value: p.value,
          label: p.label,
          valueType: typeof p.value,
          labelType: typeof p.label
        })));
        setProductNames(products);
        console.log('[CustomerComplaintForm] productNames state should now be set with', products.length, 'items');
      } else {
        console.warn('[CustomerComplaintForm] No products loaded from loadInitialData');
        console.warn('[CustomerComplaintForm] Products value:', products);
        console.warn('[CustomerComplaintForm] Products type:', typeof products);
        console.warn('[CustomerComplaintForm] Products length:', products?.length);
        setProductNames([]); // Explicitly set empty array
      }
      setLoadingFusionData(false);

      // Set Fusion Manage suppliers - these are the ONLY suppliers used (no Epicor suppliers)
      // Fusion Manage validates against its picklist, so only picklist suppliers are accepted
      if (fusionSuppliers && fusionSuppliers.length > 0) {
        mergeSupplierData(fusionSuppliers, {
          preferIncoming: true,
          sourceLabel: 'fusion-manage'
        });
      }
    };

    loadInitialData();

    // Cleanup function to cancel async operations
    return () => {
      console.log('[CustomerComplaintForm] loadInitialData cleanup - cancelling async operations');
      cancelled = true;
    };
  }, []); // Changed from [formData.ProjectID] to [] - only run once on mount

  // Epicor supplier fetching removed - only Fusion Manage suppliers from picklist are used
  // Fusion Manage validates against its picklist, so Epicor suppliers cannot be accepted

  // Fetch projects when ProjectOrTechnology is "Project"
  useEffect(() => {
    let cancelled = false;
    const rawValue = formData.ProjectOrTechnology;
    const selectedType = rawValue?.value || rawValue?.label || rawValue;
    const normalizedType = selectedType ? String(selectedType).trim().toLowerCase() : '';
    
    // Check if it's a project type (case-insensitive, handles variations)
    const isProjectType = normalizedType === 'project' || 
                         normalizedType.includes('project') ||
                         (rawValue?.value && String(rawValue.value).toLowerCase().includes('project')) ||
                         (rawValue?.label && String(rawValue.label).toLowerCase().includes('project'));
    
    console.log('[CustomerComplaintForm] ProjectOrTechnology changed:', {
      raw: rawValue,
      selectedType,
      normalizedType,
      isProjectType,
      willFetch: isProjectType
    });
    
    if (!isProjectType) {
      console.log('[CustomerComplaintForm] ProjectOrTechnology is not a project type, clearing projects. Current value:', normalizedType);
      setProjects([]);
      setJobs([]);
      return;
    }
    
    console.log('[CustomerComplaintForm] ProjectOrTechnology is a project type, will fetch projects from Epicor');

    const fetchProjects = async () => {
      setLoadingProjects(true);
      try {
        // Fetch projects from all companies - no company filter to allow cross-company project access
        console.log('[CustomerComplaintForm] Fetching projects from Epicor (all companies)');
        const response = await fetchGraphQL(NCRPROJECTLIST_LOOKUP_QUERY, {
          company: null // Pass null to get projects from all companies
        });
        
        if (cancelled) return;
        
        console.log('[CustomerComplaintForm] Received response from Epicor:', response);
        
        // Extract projects from response
        const allProjects = [];
        if (response && response.ncrProjectList) {
          console.log(`[CustomerComplaintForm] Found ${response.ncrProjectList.length} projects`);
          allProjects.push(...response.ncrProjectList);
        } else {
          console.warn('[CustomerComplaintForm] No projects found in response:', response);
        }
        
        console.log('[CustomerComplaintForm] Total projects:', allProjects.length);
        if (allProjects.length > 0) {
          console.log('[CustomerComplaintForm] Sample projects:', allProjects.slice(0, 3));
        }
        
        if (!cancelled) {
          setProjects(allProjects);
        }
      } catch (error) {
        console.error('[CustomerComplaintForm] Error fetching projects:', error);
      } finally {
        if (!cancelled) {
          setLoadingProjects(false);
        }
      }
    };

    fetchProjects();
    return () => { cancelled = true; };
  }, [formData.ProjectOrTechnology, fetchGraphQL]);

  // Use pre-loaded products when ProjectOrTechnology is "Product"
  // Products are already loaded in loadInitialData, so we just need to ensure they're available when Product is selected
  useEffect(() => {
    const selectedType = formData.ProjectOrTechnology?.value || formData.ProjectOrTechnology?.label || formData.ProjectOrTechnology;
    
    if (selectedType !== 'Product') {
      // Don't clear products - keep them available for when user switches back
      return;
    }

    // Products should already be loaded from loadInitialData
    // If they're not set yet, they will be set when loadInitialData completes
    // This useEffect just ensures products are available when Product is selected
    console.log('[CustomerComplaintForm] Product type selected, products should be available:', productNames.length);
  }, [formData.ProjectOrTechnology, productNames.length]);

  // Project-related useMemos - must be defined before useEffects that use them
  const formatProjectLabel = (project) => {
    if (!project) return '';
    const id = project.ProjectID !== undefined && project.ProjectID !== null
      ? String(project.ProjectID)
      : '';
    const name = project.ProjectName ? String(project.ProjectName).trim() : '';
    return name ? `${id} - ${name}` : id;
  };

  const projectLookup = useMemo(() => {
    const map = new Map();
    (projects || []).forEach(project => {
      map.set(String(project.ProjectID), project);
    });
    return map;
  }, [projects]);

  const projectIdOptions = useMemo(() => {
    const seen = new Set();
    const options = [];
    console.log('[CustomerComplaintForm] Generating projectIdOptions from', projects?.length || 0, 'projects');
    (projects || []).forEach(project => {
      const id = String(project.ProjectID);
      if (!seen.has(id)) {
        seen.add(id);
        options.push({ value: id, label: formatProjectLabel(project) });
      }
    });
    console.log('[CustomerComplaintForm] projectIdOptions generated:', options.length, 'options');
    if (options.length > 0) {
      console.log('[CustomerComplaintForm] Sample projectIdOptions:', options.slice(0, 3));
    }
    return options;
  }, [projects]);

  const projectNameOptions = useMemo(() => (projects || []).map((project) => {
    const id = String(project.ProjectID);
    const name = project.ProjectName ? String(project.ProjectName).trim() : '';
    return {
      value: id,
      label: formatProjectLabel(project), // Full string for dropdown
      projectName: name // Store name separately
    };
  }), [projects]);

  const selectedProjectId = useMemo(() => {
    const rawValue = formData.ProjectID;
    if (!rawValue) return null;
    if (typeof rawValue === 'object') {
      if (rawValue === null) return null;
      if (typeof rawValue.value !== 'undefined') return String(rawValue.value);
      return String(rawValue);
    }
    return String(rawValue);
  }, [formData.ProjectID]);

  const selectedProjectNameOption = useMemo(() => {
    if (!selectedProjectId) {
      return null;
    }
    const option = projectNameOptions.find(option => option.value === selectedProjectId) || null;
    // If option found, return it with label showing only the project name (not full string)
    if (option) {
      return {
        ...option,
        label: option.projectName || option.label // Show only project name when selected
      };
    }
    return null;
  }, [projectNameOptions, selectedProjectId]);

  useEffect(() => {
    setFormData(prev => {
      if (!selectedProjectId) {
        if (!prev.ProjectName) {
          return prev;
        }
        return { ...prev, ProjectName: '' };
      }

      const project = projectLookup.get(selectedProjectId);
      const projectName = project?.ProjectName || '';
      if (prev.ProjectName === projectName) {
        return prev;
      }
      return { ...prev, ProjectName: projectName };
    });
  }, [selectedProjectId, projectLookup, setFormData]);

  // Fetch jobs when ProjectID is selected
  useEffect(() => {
    let cancelled = false;
    const projectId = selectedProjectId;
    
    if (!projectId) {
      setJobs([]);
      return;
    }

    const fetchJobs = async () => {
      setLoadingJobs(true);
      try {
        // Fetch jobs from all companies - no company filter to allow cross-company job access
        console.log('[CustomerComplaintForm] Fetching jobs from Epicor for project:', projectId, '(all companies)');
        const response = await fetchGraphQL(NCRJOBLIST_LOOKUP_QUERY, { 
          projectID: String(projectId),
          company: null // Pass null to get jobs from all companies
        });
        
        if (cancelled) {
          console.log('[CustomerComplaintForm] Job fetch was cancelled');
          return;
        }
        
        console.log('[CustomerComplaintForm] Received jobs response from Epicor:', response);
        
        // Extract jobs from response
        const allJobs = [];
        if (response && response.ncrJobList) {
          console.log(`[CustomerComplaintForm] Found ${response.ncrJobList.length} jobs`);
          allJobs.push(...response.ncrJobList);
        } else {
          console.warn('[CustomerComplaintForm] No jobs found in response:', response);
        }
        
        console.log('[CustomerComplaintForm] Total jobs:', allJobs.length);
        if (allJobs.length > 0) {
          console.log('[CustomerComplaintForm] Sample jobs:', allJobs.slice(0, 3));
        }
        
        if (!cancelled) {
          setJobs(allJobs);
        }
      } catch (error) {
        console.error('[CustomerComplaintForm] Error fetching jobs:', error);
        console.error('[CustomerComplaintForm] Error stack:', error.stack);
      } finally {
        if (!cancelled) {
          setLoadingJobs(false);
        }
      }
    };

    fetchJobs();
    return () => { cancelled = true; };
  }, [selectedProjectId, fetchGraphQL]);

  // Suppliers are loaded from Fusion Manage only (see loadInitialData above)

  // Fetch valid Global Site options for Assigned and Raised By sections on mount
  useEffect(() => {
    let cancelled = false;
    const fetchGlobalSiteOptions = async () => {
      setLoadingGlobalSiteOptions(true);
      try {
        // Fetch valid options for GLOBAL_SITE_ASSIGNED (Assigned section)
        const assignedResponse = await fetch(`/api/fm/workspaces/${WORKSPACE_IDS.CUSTOMER_COMPLAINT}/views/1/fields/GLOBAL_SITE_ASSIGNED/options`);
        if (cancelled) return;
        
        if (assignedResponse.ok) {
          const assignedData = await assignedResponse.json();
          if (assignedData.success && assignedData.options) {
            const assignedOptions = assignedData.options.map(opt => ({
              value: opt.value || opt.label,
              label: opt.label || opt.value
            }));
            if (!cancelled) {
              setValidGlobalSiteOptionsAssigned(assignedOptions);
              console.log(`[CustomerComplaintForm] Fetched ${assignedOptions.length} valid GLOBAL_SITE_ASSIGNED options:`, assignedOptions.map(o => o.label).join(', '));
            }
          }
        }

        // Fetch valid options for GLOBAL_SITE (Raised By section)
        const raisedByResponse = await fetch(`/api/fm/workspaces/${WORKSPACE_IDS.CUSTOMER_COMPLAINT}/views/1/fields/GLOBAL_SITE/options`);
        if (cancelled) return;
        
        if (raisedByResponse.ok) {
          const raisedByData = await raisedByResponse.json();
          if (raisedByData.success && raisedByData.options) {
            const raisedByOptions = raisedByData.options.map(opt => ({
              value: opt.value || opt.label,
              label: opt.label || opt.value
            }));
            if (!cancelled) {
              setValidGlobalSiteOptionsRaisedBy(raisedByOptions);
              console.log(`[CustomerComplaintForm] Fetched ${raisedByOptions.length} valid GLOBAL_SITE (Raised By) options:`, raisedByOptions.map(o => o.label).join(', '));
            }
          }
        }
      } catch (error) {
        if (!cancelled) {
          console.error('[CustomerComplaintForm] Error fetching valid Global Site options:', error);
        }
      } finally {
        if (!cancelled) {
          setLoadingGlobalSiteOptions(false);
        }
      }
    };

    fetchGlobalSiteOptions();
    return () => { cancelled = true; };
  }, []);

  // Debounced values for filtered option fetches
  const debouncedGlobalSiteAssigned = useDebounce(formData.GlobalSiteAssigned, 300);
  const debouncedSiteAssigned = useDebounce(formData.SiteAssigned, 300);
  const debouncedDepartmentAssigned = useDebounce(formData.DepartmentAssigned, 300);
  const debouncedGlobalSiteRaisedBy = useDebounce(formData.GlobalSiteRaisedBy, 300);
  const debouncedSiteRaisedBy = useDebounce(formData.SiteRaisedBy, 300);

  // Fetch filtered SITE_ASSIGNED options when GLOBAL_SITE_ASSIGNED changes (with debouncing)
  useEffect(() => {
    let cancelled = false;
    const fetchSiteOptions = async () => {
      const globalSite = debouncedGlobalSiteAssigned?.value || debouncedGlobalSiteAssigned;
      if (!globalSite) {
        setFilteredSiteOptionsAssigned([]);
        return;
      }

      setLoadingFilteredOptions(true);
      try {
        const queryParams = new URLSearchParams({ GLOBAL_SITE_ASSIGNED: globalSite });
        const response = await fetch(`/api/fm/workspaces/${WORKSPACE_IDS.CUSTOMER_COMPLAINT}/views/1/fields/SITE_ASSIGNED/options?${queryParams.toString()}`);
        if (cancelled) return;
        
        if (response.ok) {
          const data = await response.json();
          if (data.success && data.options) {
            const options = data.options.map(opt => ({
              value: opt.value || opt.label,
              label: opt.label || opt.value
            }));
            if (!cancelled) {
              setFilteredSiteOptionsAssigned(options);
              console.log(`[CustomerComplaintForm] Fetched ${options.length} filtered SITE_ASSIGNED options for GLOBAL_SITE_ASSIGNED: ${globalSite}`);
            }
          }
        }
      } catch (error) {
        if (!cancelled) {
          console.error('[CustomerComplaintForm] Error fetching filtered SITE_ASSIGNED options:', error);
          setFilteredSiteOptionsAssigned([]);
        }
      } finally {
        if (!cancelled) {
          setLoadingFilteredOptions(false);
        }
      }
    };

    fetchSiteOptions();
    return () => { cancelled = true; };
  }, [debouncedGlobalSiteAssigned]);

  // Fetch filtered DEPARTMENT_ASSIGNED options when GLOBAL_SITE_ASSIGNED or SITE_ASSIGNED changes (with debouncing)
  useEffect(() => {
    let cancelled = false;
    const fetchDepartmentOptions = async () => {
      const globalSite = debouncedGlobalSiteAssigned?.value || debouncedGlobalSiteAssigned;
      const site = debouncedSiteAssigned?.value || debouncedSiteAssigned;
      
      if (!globalSite || !site) {
        setFilteredDepartmentOptionsAssigned([]);
        return;
      }

      setLoadingFilteredOptions(true);
      try {
        const queryParams = new URLSearchParams({
          GLOBAL_SITE_ASSIGNED: globalSite,
          SITE_ASSIGNED: site
        });
        const response = await fetch(`/api/fm/workspaces/${WORKSPACE_IDS.CUSTOMER_COMPLAINT}/views/1/fields/DEPARTMENT_ASSIGNED/options?${queryParams.toString()}`);
        if (cancelled) return;
        
        if (response.ok) {
          const data = await response.json();
          if (data.success && data.options) {
            const options = data.options.map(opt => ({
              value: opt.value || opt.label,
              label: opt.label || opt.value
            }));
            if (!cancelled) {
              setFilteredDepartmentOptionsAssigned(options);
              console.log(`[CustomerComplaintForm] Fetched ${options.length} filtered DEPARTMENT_ASSIGNED options for GLOBAL_SITE_ASSIGNED: ${globalSite}, SITE_ASSIGNED: ${site}`);
            }
          }
        }
      } catch (error) {
        if (!cancelled) {
          console.error('[CustomerComplaintForm] Error fetching filtered DEPARTMENT_ASSIGNED options:', error);
          setFilteredDepartmentOptionsAssigned([]);
        }
      } finally {
        if (!cancelled) {
          setLoadingFilteredOptions(false);
        }
      }
    };

    fetchDepartmentOptions();
    return () => { cancelled = true; };
  }, [debouncedGlobalSiteAssigned, debouncedSiteAssigned]);

  // Auto-derive GlobalSiteAssigned when SiteAssigned changes
  useEffect(() => {
    const selectedSite = formData.SiteAssigned?.value || formData.SiteAssigned;
    if (!selectedSite) {
      return; // Don't clear global site if user is just changing selection
    }

    const allSites = parsedLocations.sites.length > 0
      ? parsedLocations.sites
      : extractUniqueSites(fusionLocations);

    const derivedGlobalSite = deriveGlobalSiteFromSite(allSites, selectedSite);

    if (derivedGlobalSite) {
      setFormData(prev => ({
        ...prev,
        GlobalSiteAssigned: derivedGlobalSite
      }));
    }
  }, [formData.SiteAssigned, parsedLocations.sites, fusionLocations]);

  // Auto-derive GlobalSiteRaisedBy when SiteRaisedBy changes
  useEffect(() => {
    const selectedSite = formData.SiteRaisedBy?.value || formData.SiteRaisedBy;
    if (!selectedSite) {
      return;
    }

    const allSites = parsedLocations.sites.length > 0
      ? parsedLocations.sites
      : extractUniqueSites(fusionLocations);

    const derivedGlobalSite = deriveGlobalSiteFromSite(allSites, selectedSite);

    if (derivedGlobalSite) {
      setFormData(prev => ({
        ...prev,
        GlobalSiteRaisedBy: derivedGlobalSite
      }));
    }
  }, [formData.SiteRaisedBy, parsedLocations.sites, fusionLocations]);

  // Fetch filtered NCR_CHAMPION_ASSIGNED options when GLOBAL_SITE_ASSIGNED, SITE_ASSIGNED, or DEPARTMENT_ASSIGNED changes (with debouncing)
  useEffect(() => {
    let cancelled = false;
    const fetchChampionOptions = async () => {
      const globalSite = debouncedGlobalSiteAssigned?.value || debouncedGlobalSiteAssigned;
      const site = debouncedSiteAssigned?.value || debouncedSiteAssigned;
      const department = debouncedDepartmentAssigned?.value || debouncedDepartmentAssigned;
      
      if (!globalSite || !site || !department) {
        setFilteredChampionOptionsAssigned([]);
        return;
      }

      setLoadingFilteredOptions(true);
      try {
        const queryParams = new URLSearchParams({
          GLOBAL_SITE_ASSIGNED: globalSite,
          SITE_ASSIGNED: site,
          DEPARTMENT_ASSIGNED: department
        });
        const response = await fetch(`/api/fm/workspaces/${WORKSPACE_IDS.CUSTOMER_COMPLAINT}/views/1/fields/NCR_CHAMPION_ASSIGNED/options?${queryParams.toString()}`);
        if (cancelled) return;
        
        if (response.ok) {
          const data = await response.json();
          if (data.success && data.options) {
            const options = data.options.map(opt => ({
              value: opt.value || opt.id || opt.label,
              label: opt.label || opt.value
            }));
            if (!cancelled) {
              setFilteredChampionOptionsAssigned(options);
              console.log(`[CustomerComplaintForm] Fetched ${options.length} filtered NCR_CHAMPION_ASSIGNED options`);
            }
          }
        }
      } catch (error) {
        if (!cancelled) {
          console.error('[CustomerComplaintForm] Error fetching filtered NCR_CHAMPION_ASSIGNED options:', error);
          setFilteredChampionOptionsAssigned([]);
        }
      } finally {
        if (!cancelled) {
          setLoadingFilteredOptions(false);
        }
      }
    };

    fetchChampionOptions();
    return () => { cancelled = true; };
  }, [debouncedGlobalSiteAssigned, debouncedSiteAssigned, debouncedDepartmentAssigned]);

  // Fetch filtered NCR_CAPABILITY_ASSIGNED options when GLOBAL_SITE_ASSIGNED, SITE_ASSIGNED, or DEPARTMENT_ASSIGNED changes (with debouncing)
  useEffect(() => {
    let cancelled = false;
    const fetchCapabilityOptions = async () => {
      const globalSite = debouncedGlobalSiteAssigned?.value || debouncedGlobalSiteAssigned;
      const site = debouncedSiteAssigned?.value || debouncedSiteAssigned;
      const department = debouncedDepartmentAssigned?.value || debouncedDepartmentAssigned;
      
      if (!globalSite || !site || !department) {
        setFilteredCapabilityOptionsAssigned([]);
        return;
      }

      setLoadingFilteredOptions(true);
      try {
        const queryParams = new URLSearchParams({
          GLOBAL_SITE_ASSIGNED: globalSite,
          SITE_ASSIGNED: site,
          DEPARTMENT_ASSIGNED: department
        });
        const response = await fetch(`/api/fm/workspaces/${WORKSPACE_IDS.CUSTOMER_COMPLAINT}/views/1/fields/NCR_CAPABILITY_ASSIGNED/options?${queryParams.toString()}`);
        if (cancelled) return;
        
        if (response.ok) {
          const data = await response.json();
          if (data.success && data.options) {
            const options = data.options.map(opt => ({
              value: opt.value || opt.id || opt.label,
              label: opt.label || opt.value
            }));
            if (!cancelled) {
              setFilteredCapabilityOptionsAssigned(options);
              console.log(`[CustomerComplaintForm] Fetched ${options.length} filtered NCR_CAPABILITY_ASSIGNED options`);
            }
          }
        }
      } catch (error) {
        if (!cancelled) {
          console.error('[CustomerComplaintForm] Error fetching filtered NCR_CAPABILITY_ASSIGNED options:', error);
          setFilteredCapabilityOptionsAssigned([]);
        }
      } finally {
        if (!cancelled) {
          setLoadingFilteredOptions(false);
        }
      }
    };

    fetchCapabilityOptions();
    return () => { cancelled = true; };
  }, [debouncedGlobalSiteAssigned, debouncedSiteAssigned, debouncedDepartmentAssigned]);

  // Fetch filtered QA_EMAIL options when GLOBAL_SITE_ASSIGNED, SITE_ASSIGNED, or DEPARTMENT_ASSIGNED changes (with debouncing)
  useEffect(() => {
    let cancelled = false;
    const fetchQAEmailOptions = async () => {
      const globalSite = debouncedGlobalSiteAssigned?.value || debouncedGlobalSiteAssigned;
      const site = debouncedSiteAssigned?.value || debouncedSiteAssigned;
      const department = debouncedDepartmentAssigned?.value || debouncedDepartmentAssigned;
      
      if (!globalSite || !site || !department) {
        setFilteredQAEmailOptionsAssigned([]);
        return;
      }

      setLoadingFilteredOptions(true);
      try {
        const queryParams = new URLSearchParams({
          GLOBAL_SITE_ASSIGNED: globalSite,
          SITE_ASSIGNED: site,
          DEPARTMENT_ASSIGNED: department
        });
        const response = await fetch(`/api/fm/workspaces/${WORKSPACE_IDS.CUSTOMER_COMPLAINT}/views/1/fields/QA_EMAIL/options?${queryParams.toString()}`);
        if (cancelled) return;
        
        if (response.ok) {
          const data = await response.json();
          if (data.success && data.options) {
            const options = data.options.map(opt => ({
              value: opt.value || opt.id || opt.label,
              label: opt.label || opt.value
            }));
            if (!cancelled) {
              setFilteredQAEmailOptionsAssigned(options);
              console.log(`[CustomerComplaintForm] Fetched ${options.length} filtered QA_EMAIL options`);
            }
          }
        }
      } catch (error) {
        if (!cancelled) {
          console.error('[CustomerComplaintForm] Error fetching filtered QA_EMAIL options:', error);
          setFilteredQAEmailOptionsAssigned([]);
        }
      } finally {
        if (!cancelled) {
          setLoadingFilteredOptions(false);
        }
      }
    };

    fetchQAEmailOptions();
    return () => { cancelled = true; };
  }, [debouncedGlobalSiteAssigned, debouncedSiteAssigned, debouncedDepartmentAssigned]);

  // Fetch filtered SITE options for "Raised By" when GLOBAL_SITE changes (with debouncing)
  useEffect(() => {
    let cancelled = false;
    const fetchSiteOptionsRaisedBy = async () => {
      const globalSite = debouncedGlobalSiteRaisedBy?.value || debouncedGlobalSiteRaisedBy;
      if (!globalSite) {
        setFilteredSiteOptionsRaisedBy([]);
        return;
      }

      setLoadingFilteredOptions(true);
      try {
        const queryParams = new URLSearchParams({ GLOBAL_SITE: globalSite });
        const response = await fetch(`/api/fm/workspaces/${WORKSPACE_IDS.CUSTOMER_COMPLAINT}/views/1/fields/SITE/options?${queryParams.toString()}`);
        if (cancelled) return;
        
        if (response.ok) {
          const data = await response.json();
          if (data.success && data.options) {
            const options = data.options.map(opt => ({
              value: opt.value || opt.label,
              label: opt.label || opt.value
            }));
            if (!cancelled) {
              setFilteredSiteOptionsRaisedBy(options);
              console.log(`[CustomerComplaintForm] Fetched ${options.length} filtered SITE (Raised By) options`);
            }
          }
        }
      } catch (error) {
        if (!cancelled) {
          console.error('[CustomerComplaintForm] Error fetching filtered SITE (Raised By) options:', error);
          setFilteredSiteOptionsRaisedBy([]);
        }
      } finally {
        if (!cancelled) {
          setLoadingFilteredOptions(false);
        }
      }
    };

    fetchSiteOptionsRaisedBy();
    return () => { cancelled = true; };
  }, [debouncedGlobalSiteRaisedBy]);

  // Fetch filtered DEPARTMENT options for "Raised By" when GLOBAL_SITE or SITE changes (with debouncing)
  useEffect(() => {
    let cancelled = false;
    const fetchDepartmentOptionsRaisedBy = async () => {
      const globalSite = debouncedGlobalSiteRaisedBy?.value || debouncedGlobalSiteRaisedBy;
      const site = debouncedSiteRaisedBy?.value || debouncedSiteRaisedBy;
      
      if (!globalSite) {
        setFilteredDepartmentOptionsRaisedBy([]);
        return;
      }

      setLoadingFilteredOptions(true);
      try {
        const queryParams = new URLSearchParams({
          GLOBAL_SITE: globalSite
        });
        if (site) {
          queryParams.append('SITE', site);
        }
        const response = await fetch(`/api/fm/workspaces/${WORKSPACE_IDS.CUSTOMER_COMPLAINT}/views/1/fields/DEPARTMENT/options?${queryParams.toString()}`);
        if (cancelled) return;
        
        if (response.ok) {
          const data = await response.json();
          if (data.success && data.options) {
            const options = data.options.map(opt => ({
              value: opt.value || opt.label,
              label: opt.label || opt.value
            }));
            if (!cancelled) {
              setFilteredDepartmentOptionsRaisedBy(options);
              console.log(`[CustomerComplaintForm] Fetched ${options.length} filtered DEPARTMENT (Raised By) options`);
            }
          }
        }
      } catch (error) {
        if (!cancelled) {
          console.error('[CustomerComplaintForm] Error fetching filtered DEPARTMENT (Raised By) options:', error);
          setFilteredDepartmentOptionsRaisedBy([]);
        }
      } finally {
        if (!cancelled) {
          setLoadingFilteredOptions(false);
        }
      }
    };

    fetchDepartmentOptionsRaisedBy();
    return () => { cancelled = true; };
  }, [debouncedGlobalSiteRaisedBy, debouncedSiteRaisedBy]);

  const handleSupplierChange = (selectedOption) => {
    const selectedSupplier = suppliers.find(supplier => supplier.VendorNum.toString() === selectedOption.value);
    setFormData(prevFormData => ({
      ...prevFormData,
      SupplierName: selectedSupplier ? selectedSupplier.VendorName : '',
      SupplierNumber: selectedSupplier ? selectedSupplier.VendorNum : '',
    }));
  };

  const handleReasonChange = (selectedOption) => {
    const selectedReason = reasons.find(reason => reason.value.toString() === selectedOption.value);
    setFormData(prevFormData => ({
      ...prevFormData,
      NCRReason: selectedReason ? selectedReason.label : '',
      NCRReasonID: selectedReason ? selectedReason.value : '',
    }));
  };

  const handleSourceChange = (selectedOption) => {
    const selectedSource = sources.find(source => source.value.toString() === selectedOption.value);
    setFormData(prevFormData => ({
      ...prevFormData,
      SourceType: selectedSource ? selectedSource.label : '',
      SourceTypeID: selectedSource ? selectedSource.value : '',
    }));
  };

  // Validation function for enhanced form
  const getMissingRequiredFields = useCallback((data) => {
    const missing = [];

    // Required fields regardless of Project or Technology Customer Complaint
    if (!data.ProjectOrTechnology) missing.push('Type');
    if (!data.Market) missing.push('Market');
    if (!data.NCRTitle) missing.push('Customer Complaint Title');
    if (!data.Description) missing.push('Description');
    if (!data.IncidentDate) missing.push('Incident Date');
    if (!data.InternalOrExternal) missing.push('Internal or External');
    // Related Supplier is not required
    if (!data.CostOfNCR) missing.push('Hours Associated');
    if (!data.GlobalSiteAssigned) missing.push('Global Assigned Location');
    if (!data.SiteAssigned) missing.push('Local Assigned Location');
    if (!data.DepartmentAssigned) missing.push('Department Assigned');
    if (!data.NCRChampionAssigned) missing.push('Customer Complaint Champion Assigned');
    if (!data.NCRCapabilityAssigned) missing.push('NCR Capability Assigned');
    if (!data.GlobalSiteRaisedBy) missing.push('Global Location Raised By');
    if (!data.SiteRaisedBy) missing.push('Local Site Raise By');
    if (!data.DepartmentRaisedBy) missing.push('Department Raised By');
    // Note: For workspace 736, SPAWNED_BY (Raised By) is read-only, so we don't require it
    // The form field is still shown but won't be submitted for workspace 736
    // if (!data.RaisedBy) missing.push('Raised By');
    if (!data.RaisedDate) missing.push('Raised Date');

    // Project-related validation
    if (data.ProjectOrTechnology === 'Project') {
      if (!data.ProjectID) missing.push('Project ID (Project Customer Complaint)');
      if (!data.ProjectName) missing.push('Project Name (Project Customer Complaint)');
    }

    // Technology-related validation
    if (data.ProjectOrTechnology === 'Technology') {
      if (!data.ProductName) missing.push('Product Name (Technology Customer Complaint)');
      if (!data.AffectedQuantity || (typeof data.AffectedQuantity === 'string' && data.AffectedQuantity.trim() === '')) {
        missing.push('Affected Quantity (Technology Customer Complaint)');
      }
    }

    return missing;
  }, []);

  // Wrapper for success callback that also resets the form
  const handleFormSubmitSuccess = useCallback((complaintNumber) => {
    // Reset form data
    setFormData({
      ...initialState,
      RaisedDate: new Date().toISOString().split('T')[0]
    });
    // Call original success callback
    onFormSubmitSuccess(complaintNumber);
  }, [initialState, onFormSubmitSuccess]);

  // Use shared form submission hook (called after getMissingRequiredFields is defined)
  const {
    handleSubmit: handleFormSubmit,
    isSubmitting,
    errorMessage,
    showErrorAlert,
    setShowErrorAlert,
    hdCaseNum,
    setHdCaseNum,
    hasInteracted,
    setHasInteracted,
    validationAttempted,
    setValidationAttempted,
    missingRequiredFields,
    setMissingRequiredFields
  } = useFormSubmission(
    'CustomerComplaint',
    getMissingRequiredFields,
    initialState,
    handleFormSubmitSuccess,
    onFormSubmitError
  );
  
  // Only compute canSubmit based on current formData, but don't show alert until submit is clicked
  const canSubmit = useMemo(() => {
    const missing = getMissingRequiredFields(formData);
    return missing.length === 0;
  }, [formData, getMissingRequiredFields]);
  
  const shouldShowMissingAlert = validationAttempted && missingRequiredFields.length > 0;

  // Use the hook's handleSubmit directly - it handles validation, submission, and callbacks
  const handleSubmit = () => handleFormSubmit(formData);
  
  // Legacy handleSubmit code removed - using hook above
  async function _handleSubmit_OLD() {
    setHasInteracted(true);
    setValidationAttempted(true);
    
    // Validate form before submission
    const validationErrors = getMissingRequiredFields(formData);
    setMissingRequiredFields(validationErrors);
    
    if (validationErrors.length > 0) {
      setErrorMessage(`Please complete the required fields: ${validationErrors.join(', ')}`);
      setShowErrorAlert(true);
      setTimeout(() => setShowErrorAlert(false), 5000);
      return;
    }

    setIsSubmitting(true);

    try {
      // Prepare the data for submission
      // Build submissionData with ONLY schema-compliant fields
      // Map new form fields to legacy GraphQL NCRInput schema fields
      // This ensures GraphQL validation passes (same as NCR form)
      const submissionData = {
        // Map new fields to legacy schema fields
        CompanyID: formData.GlobalSiteAssigned?.value || formData.GlobalSiteAssigned || '',
        ProjectID: formData.ProjectID?.value || formData.ProjectID,
        JobCode: formData.JobNumber?.value || formData.JobNumber,
        SourceTypeID: formData.SourceTypeID || '',
        SourceType: formData.SourceType || '',
        CaseTitle: formData.NCRTitle || formData.CaseTitle,
        CaseSummary: formData.Description || formData.CaseSummary,
        Market: formData.Market?.value || formData.Market?.label || formData.Market,
        SupplierNumber: formData.SupplierNumber || '',
        SupplierName: formData.SupplierName || '',
        IncidentDate: formData.IncidentDate || '',
        IncidentLocation: formData.IncidentLocation || '',
        OtherLocation: formData.OtherLocation || '',
        IncidentArea: formData.IncidentArea || '',
        NCRChampionID: formData.NCRChampionAssigned?.value || formData.NCRChampionAssigned || formData.NCRChampionID,
        NCRChampion: formData.NCRChampion || '',
        CaseTypeID: formData.CaseTypeID || '',
        CaseType: formData.CaseType || '',
        ProjectRelated: formData.ProjectRelated !== undefined ? formData.ProjectRelated : true,
        SafetyCritical: formData.SafetyCritical !== undefined ? formData.SafetyCritical : false,
        LegalCompliance: formData.LegalCompliance !== undefined ? formData.LegalCompliance : false,
        NCRReason: formData.NCRReason || '',
        NCRInitiator: formData.NCRInitiator || '',
        Notes: formData.Notes || '',
        NCRReasonID: formData.NCRReasonID || '',
        NCRInitiatorID: formData.RaisedBy || formData.NCRInitiatorID
      };

      // Prepare complete form data for submission (same structure as NCR form)
      const completeFormData = {
        ...submissionData,
        // Include ALL new form fields for Fusion Manage sync
        // Extract .value from ReactSelect objects
        // Note: Number will be generated by Fusion Manage
        ProjectOrTechnology: formData.ProjectOrTechnology?.label
          || formData.ProjectOrTechnology?.value
          || formData.ProjectOrTechnology,
        Market: formData.Market?.value || formData.Market?.label || formData.Market,
        NCRTitle: formData.NCRTitle,
        Description: formData.Description,
        // InternalOrExternal: Send as object with both value and label for better backend matching
        InternalOrExternal: formData.InternalOrExternal
          ? (typeof formData.InternalOrExternal === 'object' && formData.InternalOrExternal !== null
              ? {
                  value: formData.InternalOrExternal.value || formData.InternalOrExternal.label || formData.InternalOrExternal,
                  label: formData.InternalOrExternal.label || formData.InternalOrExternal.value || formData.InternalOrExternal
                }
              : {
                  value: formData.InternalOrExternal,
                  label: formData.InternalOrExternal
                })
          : null,
        // RelatedSupplier: Send as object with both value and label for better backend matching
        RelatedSupplier: (() => {
          if (!formData.RelatedSupplier) {
            console.log('[CustomerComplaintForm] RelatedSupplier is empty/null, not including in submission');
            return null;
          }
          
          // Handle object format (standard case for single select)
          if (typeof formData.RelatedSupplier === 'object' && !Array.isArray(formData.RelatedSupplier)) {
            const result = {
              value: formData.RelatedSupplier.value || formData.RelatedSupplier,
              label: formData.RelatedSupplier.label || formData.RelatedSupplier.value || formData.RelatedSupplier
            };
            console.log('[CustomerComplaintForm] RelatedSupplier being sent:', result);
            return result;
          }
          
          // Handle string/number format (fallback for backward compatibility)
          if (formData.RelatedSupplier) {
            console.log('[CustomerComplaintForm] RelatedSupplier (primitive format) being sent:', formData.RelatedSupplier);
            return {
              value: formData.RelatedSupplier,
              label: formData.RelatedSupplier
            };
          }
          
          return null;
        })(),
        CostOfNCR: formData.CostOfNCR ? parseFloat(formData.CostOfNCR) : null,
        ShippingCost: formData.ShippingCost ? parseFloat(formData.ShippingCost) : null,
        MaterialCost: formData.MaterialCost ? parseFloat(formData.MaterialCost) : null,
        LaborCost: formData.LaborCost ? parseFloat(formData.LaborCost) : null,
        // Fusion Manage filtered fields need BOTH value (ID) and label (text)
        // Send as object so backend can use label for v3 API
        GlobalSiteAssigned: formData.GlobalSiteAssigned?.value
          ? { value: formData.GlobalSiteAssigned.value, label: formData.GlobalSiteAssigned.label }
          : formData.GlobalSiteAssigned,
        SiteAssigned: formData.SiteAssigned?.value
          ? { value: formData.SiteAssigned.value, label: formData.SiteAssigned.label }
          : formData.SiteAssigned,
        DepartmentAssigned: formData.DepartmentAssigned?.value
          ? { value: formData.DepartmentAssigned.value, label: formData.DepartmentAssigned.label }
          : formData.DepartmentAssigned,
        NCRChampionAssigned: formData.NCRChampionAssigned?.value
          ? { value: formData.NCRChampionAssigned.value, label: formData.NCRChampionAssigned.label }
          : formData.NCRChampionAssigned,
        NCRCapabilityAssigned: formData.NCRCapabilityAssigned?.value
          ? { value: formData.NCRCapabilityAssigned.value, label: formData.NCRCapabilityAssigned.label }
          : formData.NCRCapabilityAssigned,
        QAEmail: formData.QAEmail?.value || formData.QAEmail?.label || formData.QAEmail,
        GlobalSiteRaisedBy: formData.GlobalSiteRaisedBy?.value
          ? { value: formData.GlobalSiteRaisedBy.value, label: formData.GlobalSiteRaisedBy.label }
          : formData.GlobalSiteRaisedBy,
        SiteRaisedBy: formData.SiteRaisedBy?.value
          ? { value: formData.SiteRaisedBy.value, label: formData.SiteRaisedBy.label }
          : formData.SiteRaisedBy,
        DepartmentRaisedBy: formData.DepartmentRaisedBy?.value
          ? { value: formData.DepartmentRaisedBy.value, label: formData.DepartmentRaisedBy.label }
          : formData.DepartmentRaisedBy,
        RaisedBy: formData.RaisedBy,
        RaisedDate: formData.RaisedDate,
        IncidentDate: formData.IncidentDate,
        ProductName: (() => {
          // Always submit the label text (extracted product name after dash)
          // If it's an object, prefer label, then value
          // If it's a string, use it directly (should already be extracted text)
          if (!formData.ProductName) return null;
          if (typeof formData.ProductName === 'object') {
            return formData.ProductName.label || formData.ProductName.value || null;
          }
          // If it's a string, it should already be the extracted text
          return formData.ProductName;
        })(),
        ProductVariant: formData.ProductVariant?.value || formData.ProductVariant,
        DrawingNumber: formData.DrawingNumber,
        SoftwareVersion: formData.SoftwareVersion,
        ProductSerialNumber: formData.ProductSerialNumber,
        AffectedQuantity: formData.AffectedQuantity ? parseFloat(formData.AffectedQuantity) : null,
        // Add ProjectName to submission
        ProjectName: formData.ProjectName?.value || formData.ProjectName || ''
      };

      // Submit to Fusion Manage (same endpoint as NCR form)
      // POST to /api/fm/customer-complaint endpoint which creates in Fusion Manage workspace 736
      // Customer Complaints are functionally the same as NCR forms, but with type set to "Compliance"
      console.log('[CustomerComplaintForm] Submitting to Fusion Manage ONLY via REST API...');
      console.log('[CustomerComplaintForm] RelatedSupplier in completeFormData:', completeFormData.RelatedSupplier);
      console.log('[CustomerComplaintForm] Payload:', JSON.stringify(completeFormData, null, 2));
        const response = await fetch('/api/fm/customer-complaint', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(completeFormData)
      });

      if (!response.ok) {
        throw new Error('Form submission failed');
      }

      const jsonResponse = await response.json();

      // REST API response format (same as NCR form)
      if (!jsonResponse.success) {
        throw new Error(jsonResponse.error || 'Failed to create Customer Complaint in Fusion Manage');
      }

      const complaintNumber = jsonResponse.Number;  // REST API returns Number
      const fusionStatus = jsonResponse.fusionManageStatus || 'success';
      const fusionMessage = jsonResponse.fusionManageMessage || 'Successfully created in Fusion Manage';

      setHdCaseNum(complaintNumber);

      // Handle Fusion Manage status
      if (fusionStatus) {
        setFusionManageStatus(fusionStatus);
        setFusionManageMessage(fusionMessage);
      }

      // Reset form and show success with complaint number from Fusion Manage
      setFormData({
        ...initialState,
        RaisedDate: new Date().toISOString().split('T')[0]
      });
      setHasInteracted(false);
      
      // Call success callback with complaint number from Fusion Manage
      onFormSubmitSuccess(complaintNumber);

    } catch (error) {
      console.error('Error:', error);
      setErrorMessage(error.message || 'An error occurred while submitting the form.');
      setShowErrorAlert(true);
      onFormSubmitError(error.message || 'An error occurred while submitting the form.');
      setTimeout(() => setShowErrorAlert(false), 5000);
    } finally {
      setIsSubmitting(false);
    }
  }

  const jobOptions = (jobs || []).map((job) => ({
    value: job.JobNum,
    label: `${job.JobNum} ${job.JobDescription}`,
  }));

  // Old locationOptions removed - now using parsed location filtering
  // const locationOptions = (locations || []).map((location) => ({
  //   value: location.value,
  //   label: location.label,
  // }));

  const areaOptions = (areas || []).map((area) => ({
    value: area.value,
    label: area.label,
  }));

  const sanitizeChampionOption = (option) => {
    if (!option) return option;
    const rawLabel = option.label || option.value || '';
    const cleanedLabel = rawLabel.split(' - ')[0]?.trim() || rawLabel;
    return {
      ...option,
      value: String(option.value || option.id || cleanedLabel),
      label: cleanedLabel
    };
  };

  const championOptions = (champions || []).map((champion) => sanitizeChampionOption({
    value: champion.value,
    label: champion.label,
  }));

  const reasonOptions = (reasons || []).map((reason) => ({
    value: reason.value,
    label: reason.label,
  }));

  const sourceOptions = (sources || []).map((source) => ({
    value: source.value,
    label: source.label,
  }));

  // Memoize supplier options from Fusion Manage picklist (for initial display)
  const supplierOptions = useMemo(() => {
    // Suppliers are normalized by mergeSupplierLists to have VendorNum and VendorName
    // But Fusion Manage suppliers come as {value, label, id} format
    let options = (suppliers || []).map((supplier) => {
      // Handle both formats: normalized {VendorNum, VendorName} and Fusion Manage {value, label}
      if (supplier.VendorNum && supplier.VendorName) {
        return {
          value: String(supplier.VendorNum),
          label: supplier.VendorName
        };
      } else if (supplier.value && supplier.label) {
        // Fusion Manage format - use directly
        return {
          value: String(supplier.value),
          label: supplier.label
        };
      } else {
        // Fallback for any other format
        return {
          value: String(supplier.value || supplier.VendorNum || supplier.id || ''),
          label: String(supplier.label || supplier.VendorName || supplier.name || '')
        };
      }
    }).filter(opt => opt.value && opt.label); // Filter out invalid options

    // Note: supplierOptions may be empty during initial render - that's OK!
    // The AsyncCreatable dropdown fetches fresh data via loadSupplierOptions when opened
    return options;
  }, [suppliers]);

  // Async function to load suppliers from Epicor (searches across all companies)
  const loadSupplierOptions = useCallback((inputValue, callback) => {
    // Use callback pattern for better compatibility with react-select
    const loadOptions = async () => {
      // Always fetch Fusion Manage suppliers directly for fresh data
      let fusionSuppliers = [];
      try {
        const fusionSuppliersData = await fetchFusionManagePicklistDirect('CUSTOM_LOOKUP_WS_SUPPLIERS');
        if (fusionSuppliersData && fusionSuppliersData.length > 0) {
          fusionSuppliers = fusionSuppliersData.map(opt => ({
            value: String(opt.value || opt.id || ''),
            label: opt.label || opt.name || String(opt.value || opt.id || ''),
            isEpicor: false,
            canCreate: false
          }));
          console.log('[CustomerComplaintForm] Fetched', fusionSuppliers.length, 'Fusion Manage suppliers from API');
        } else {
          console.warn('[CustomerComplaintForm] No Fusion Manage suppliers returned from API');
        }
      } catch (error) {
        console.error('[CustomerComplaintForm] Error fetching Fusion Manage suppliers:', error);
      }
      
      try {
        // Fetch from Epicor with search term
        const response = await fetchGraphQL(NCRSUPPLIERS_LOOKUP_QUERY, {
          company: null, // null means aggregate from all companies
          search: inputValue || ''
        });

        if (!response || !response.ncrSupplierList) {
          // Return Fusion Manage suppliers even if Epicor fetch fails
          console.log('[CustomerComplaintForm] Epicor fetch failed or empty, returning', fusionSuppliers.length, 'Fusion Manage suppliers');
          callback(null, { options: fusionSuppliers });
          return;
        }

        // Filter out suppliers without IDs (VendorNum)
        const epicorSuppliers = (response.ncrSupplierList || [])
          .filter(supplier => supplier.VendorNum && String(supplier.VendorNum).trim() !== '')
          .map(supplier => ({
            value: String(supplier.VendorNum),
            label: supplier.VendorName || String(supplier.VendorNum),
            isEpicor: true
          }));

        // Fusion Manage suppliers already defined above

        // Create maps for quick lookup - normalize for comparison
        // Use maps to store the actual supplier objects for priority handling
        const fusionSupplierByLabel = new Map();
        const fusionSupplierByValue = new Map();
        
        fusionSuppliers.forEach(supplier => {
          const label = String(supplier.label || supplier.value || '').trim().toLowerCase();
          const value = String(supplier.value || '').trim().toLowerCase();
          if (label.length > 0) {
            fusionSupplierByLabel.set(label, supplier);
          }
          if (value.length > 0) {
            fusionSupplierByValue.set(value, supplier);
          }
        });

        // Process Epicor suppliers: merge with picklist, picklist takes priority for duplicates
        // Deduplicate Epicor suppliers by value first (in case same vendor appears in multiple companies)
        const epicorSupplierMap = new Map();
        epicorSuppliers.forEach(supplier => {
          const key = String(supplier.value).trim().toLowerCase();
          if (!epicorSupplierMap.has(key)) {
            epicorSupplierMap.set(key, supplier);
          }
        });

        // Now filter out Epicor suppliers that match picklist suppliers (picklist takes priority)
        // But keep Epicor suppliers that don't exist in picklist
        const uniqueEpicorSuppliers = [];
        epicorSupplierMap.forEach((epicorSupplier, key) => {
          const epicorLabel = String(epicorSupplier.label || '').trim().toLowerCase();
          const epicorValue = String(epicorSupplier.value || '').trim().toLowerCase();
          
          // Check if this Epicor supplier matches a picklist supplier
          const matchesByLabel = epicorLabel.length > 0 && fusionSupplierByLabel.has(epicorLabel);
          const matchesByValue = epicorValue.length > 0 && fusionSupplierByValue.has(epicorValue);
          
          // Only add Epicor supplier if it doesn't match a picklist supplier
          // (Picklist suppliers are already included, so we skip duplicates)
          if (!matchesByLabel && !matchesByValue) {
            uniqueEpicorSuppliers.push({
              ...epicorSupplier,
              canCreate: true  // These can be "added" (created in Workspace 14)
            });
          }
        });

        // Merge: Picklist suppliers first (they take priority), then unique Epicor suppliers
        const allSuppliers = [...fusionSuppliers, ...uniqueEpicorSuppliers];

        callback(null, { options: allSuppliers });
      } catch (error) {
        console.error('[CustomerComplaintForm] Error loading supplier options:', error);
        // Return Fusion Manage suppliers as fallback
        callback(null, { options: fusionSuppliers || [] });
      }
    };

    loadOptions();
  }, [fetchGraphQL]); // Removed supplierOptions dependency - always fetch fresh data

  const initiatorOptions = (initiator || []).map((init) => ({
    value: init.value,
    label: init.label,
  }));

  // Use Fusion Manage picklist directly for Type field
  const typeOptions = fusionProjectOrTechnology || [];

  const internalOrExternalOptions = mergePicklists(
    [
      { value: 'Internal', label: 'Internal' },
      { value: 'External', label: 'External' }
    ],
    fusionInternalOrExternal
  );

  const productNameOptions = useMemo(() => {
    if (!productNames || productNames.length === 0) {
      return [];
    }

    // Helper function to extract text after "-" and trim whitespace (same as NCR form)
    const extractAfterDash = (str) => {
      if (!str || typeof str !== 'string') return str;
      const dashIndex = str.indexOf(' - ');
      if (dashIndex !== -1) {
        return str.substring(dashIndex + 3).trim();
      }
      // Also check for single dash without spaces
      const singleDashIndex = str.indexOf('-');
      if (singleDashIndex !== -1) {
        return str.substring(singleDashIndex + 1).trim();
      }
      return str.trim();
    };

    const options = productNames.map((product, index) => {
      // If value is a numeric ID, extract from label instead
      // Otherwise extract from both value and label
      const isNumericId = /^\d+$/.test(String(product.value || ''));
      const sourceForExtraction = isNumericId ? (product.label || product.value) : (product.value || product.label);
      const extractedText = extractAfterDash(sourceForExtraction);
      
      const option = {
        value: extractedText,
        label: extractedText,
      };
      if (index < 3) {
        console.log(`[CustomerComplaintForm] productNameOptions - Mapping product ${index}:`, {
          product,
          option,
          hasValue: !!product.value,
          hasLabel: !!product.label,
          extractedText
        });
      }
      return option;
    }).filter(opt => {
      const isValid = opt.value && opt.label;
      if (!isValid) {
        console.warn('[CustomerComplaintForm] productNameOptions - Filtered out invalid option:', opt);
      }
      return isValid;
    });

    return options;
  }, [productNames]);

  const productVariantOptions = (productVariants || []).map((variant) => ({
    value: variant.value,
    label: variant.label,
  }));

  // Use parsed locations with cascading filters (memoized for performance)
  const locationOptions = useMemo(() => {
    // Get selected values for filtering
    const selectedGlobalSiteAssigned = formData.GlobalSiteAssigned?.value || formData.GlobalSiteAssigned || null;
    const selectedSiteAssigned = formData.SiteAssigned?.value || formData.SiteAssigned || null;
    const selectedGlobalSiteRaisedBy = formData.GlobalSiteRaisedBy?.value || formData.GlobalSiteRaisedBy || null;
    const selectedSiteRaisedBy = formData.SiteRaisedBy?.value || formData.SiteRaisedBy || null;

    // Parse locations from Fusion Manage if available
    const parsedGlobalSites = parsedLocations.globalSites.length > 0 
      ? parsedLocations.globalSites 
      : extractUniqueGlobalSites(fusionLocations);
    
    // Show ALL sites (no filtering by global site)
    const parsedSitesAssigned = parsedLocations.sites.length > 0
      ? parsedLocations.sites
      : extractUniqueSites(fusionLocations);

    const parsedSitesRaisedBy = parsedLocations.sites.length > 0
      ? parsedLocations.sites
      : extractUniqueSites(fusionLocations);
    
    // Filter departments based on selected global site and site
    const parsedDepartmentsAssigned = (selectedGlobalSiteAssigned || selectedSiteAssigned)
      ? extractUniqueDepartments(fusionLocations, selectedGlobalSiteAssigned, selectedSiteAssigned)
      : parsedLocations.departments.length > 0 
        ? parsedLocations.departments 
        : extractUniqueDepartments(fusionLocations);
    
    const parsedDepartmentsRaisedBy = (selectedGlobalSiteRaisedBy || selectedSiteRaisedBy)
      ? extractUniqueDepartments(fusionLocations, selectedGlobalSiteRaisedBy, selectedSiteRaisedBy)
      : parsedLocations.departments.length > 0 
        ? parsedLocations.departments 
        : extractUniqueDepartments(fusionLocations);

    return {
      selectedGlobalSiteAssigned,
      selectedSiteAssigned,
      selectedGlobalSiteRaisedBy,
      selectedSiteRaisedBy,
      parsedGlobalSites,
      parsedSitesAssigned,
      parsedSitesRaisedBy,
      parsedDepartmentsAssigned,
      parsedDepartmentsRaisedBy
    };
  }, [formData.GlobalSiteAssigned, formData.SiteAssigned, formData.GlobalSiteRaisedBy, formData.SiteRaisedBy, parsedLocations, fusionLocations]);

  // Merge with Epicor data
  const epicorGlobalSites = (globalSites || []).map((site) => ({
    value: site.value,
    label: site.label,
  }));

  const epicorSites = (sites || []).map((site) => ({
    value: site.value,
    label: site.label,
  }));

  const epicorDepartments = (departments || []).map((dept) => ({
    value: dept.value,
    label: dept.label,
  }));

  // Fallback data
  const fallbackGlobalSites = [
    { value: 'UK', label: 'United Kingdom' },
    { value: 'NZ', label: 'New Zealand' },
    { value: 'US', label: 'United States' },
    { value: 'AU', label: 'Australia' }
  ];

  const fallbackSites = [
    { value: 'MAIN', label: 'Main Site' },
    { value: 'WAREHOUSE', label: 'Warehouse' },
    { value: 'PRODUCTION', label: 'Production Floor' }
  ];

  const fallbackDepartments = [
    { value: 'QA', label: 'Quality Assurance' },
    { value: 'PRODUCTION', label: 'Production' },
    { value: 'ENGINEERING', label: 'Engineering' },
    { value: 'LOGISTICS', label: 'Logistics' },
    { value: 'ADMIN', label: 'Administration' }
  ];

  // Merge parsed locations with Epicor data
  const { 
    selectedGlobalSiteAssigned, 
    selectedSiteAssigned, 
    selectedGlobalSiteRaisedBy, 
    selectedSiteRaisedBy,
    parsedGlobalSites,
    parsedSitesAssigned,
    parsedSitesRaisedBy,
    parsedDepartmentsAssigned,
    parsedDepartmentsRaisedBy
  } = locationOptions;

  // Use picklist data ONLY for global sites (filtered field options are too restrictive and exclude valid options like UK)
  // The filtered field options are meant for validation, not for populating the dropdown
  // For Assigned section: use picklist data only
  // For Raised By section: use picklist data only
  const baseGlobalSiteOptions = mergePicklists(epicorGlobalSites, parsedGlobalSites);
  
  // Use picklist data directly, don't merge with filtered options (they exclude valid options)
  let globalSiteOptionsAssigned = baseGlobalSiteOptions.length > 0
    ? baseGlobalSiteOptions
    : fallbackGlobalSites;
  if (globalSiteOptionsAssigned.length === 0) {
    globalSiteOptionsAssigned = fallbackGlobalSites;
  }

  // Use picklist data directly, don't merge with filtered options (they exclude valid options)
  let globalSiteOptionsRaisedBy = baseGlobalSiteOptions.length > 0
    ? baseGlobalSiteOptions
    : fallbackGlobalSites;
  if (globalSiteOptionsRaisedBy.length === 0) {
    globalSiteOptionsRaisedBy = fallbackGlobalSites;
  }

  // For backward compatibility, use Assigned options as default (but each section will use its own)
  let globalSiteOptions = globalSiteOptionsAssigned;

  // Use filtered options from Fusion Manage if available, otherwise fall back to parsed/Epicor data
  let siteOptionsAssigned = filteredSiteOptionsAssigned.length > 0 
    ? filteredSiteOptionsAssigned 
    : mergePicklists(epicorSites, parsedSitesAssigned);
  if (siteOptionsAssigned.length === 0) {
    siteOptionsAssigned = fallbackSites;
  }

  let siteOptionsRaisedBy = filteredSiteOptionsRaisedBy.length > 0
    ? filteredSiteOptionsRaisedBy
    : mergePicklists(epicorSites, parsedSitesRaisedBy);
  if (siteOptionsRaisedBy.length === 0) {
    siteOptionsRaisedBy = fallbackSites;
  }

  let departmentOptionsAssigned = filteredDepartmentOptionsAssigned.length > 0
    ? filteredDepartmentOptionsAssigned
    : mergePicklists(epicorDepartments, parsedDepartmentsAssigned);
  if (departmentOptionsAssigned.length === 0) {
    departmentOptionsAssigned = fallbackDepartments;
  }

  let departmentOptionsRaisedBy = filteredDepartmentOptionsRaisedBy.length > 0
    ? filteredDepartmentOptionsRaisedBy
    : mergePicklists(epicorDepartments, parsedDepartmentsRaisedBy);
  if (departmentOptionsRaisedBy.length === 0) {
    departmentOptionsRaisedBy = fallbackDepartments;
  }
  
  // For backward compatibility, also set siteOptions and departmentOptions
  const siteOptions = siteOptionsAssigned;
  const departmentOptions = departmentOptionsAssigned;

  // Merge Epicor and Fusion Manage NCR Champions
  const epicorNcrChampions = (ncrChampions || []).map((champion) => ({
    value: champion.value,
    label: champion.label,
  }));

  // Fallback data for NCR Champions if both sources are empty
  const fallbackNCRChampions = [
    { value: 'CHAMPION1', label: 'QA Champion' },
    { value: 'CHAMPION2', label: 'Production Champion' },
    { value: 'CHAMPION3', label: 'Engineering Champion' }
  ];

  // Build a broad champion list – filtered options show first, but full list remains available
  const baseChampionOptions = mergePicklists(epicorNcrChampions, fusionNCRChampions).map(sanitizeChampionOption);
  let ncrChampionOptions = filteredChampionOptionsAssigned.length > 0
    ? filteredChampionOptionsAssigned.map(sanitizeChampionOption)
    : baseChampionOptions;
  if (ncrChampionOptions.length === 0) {
    console.warn('[CustomerComplaintForm] Both Epicor NCR Champions and Fusion CUSTOM_LOOKUP_WS_QA_NCR_CHAMPIONS are empty. Using fallback data.');
    console.warn('[CustomerComplaintForm] Please configure either:');
    console.warn('[CustomerComplaintForm]   1. Epicor NCR Champions data');
    console.warn('[CustomerComplaintForm]   2. Fusion Manage Custom Lookup: CUSTOM_LOOKUP_WS_QA_NCR_CHAMPIONS');
    ncrChampionOptions = fallbackNCRChampions;
  }

  const raisedByOptions = (raisedByUsers || []).map((user) => ({
    value: user.value,
    label: user.label,
  }));

  return (
    <div className="ncr-form">
      <div>
          {shouldShowMissingAlert && (
            <Alert bsStyle="warning">
              <strong>Required fields missing:</strong>
              <ul>
                {missingRequiredFields.map(field => (
                  <li key={field}>{field}</li>
                ))}
              </ul>
            </Alert>
          )}

          {showErrorAlert && (
            <Alert bsStyle="danger" onDismiss={() => setShowErrorAlert(false)}>
              {errorMessage}
            </Alert>
          )}

          {/* Type selection */}
          <label htmlFor="ProjectOrTechnology">
            <span className="ncr-required">
              <span className="text-danger">* </span>Type:
            </span>
            <InfoTooltip message="Compliance - Any NCR not related to a specific product or project&#10;Project - Any NCR related to a project&#10;Product - An NCR related to a specific TAIT product" />
          </label>
          <ReactSelect
            id="ProjectOrTechnology"
            name="ProjectOrTechnology"
            value={typeOptions.find(opt => opt.value === (formData.ProjectOrTechnology?.value || formData.ProjectOrTechnology)) || null}
            required
            isLoading={loadingFusionData}
            onChange={(selectedOption) =>
              handleSelectChange(selectedOption, 'ProjectOrTechnology')
            }
            options={typeOptions}
            placeholder="Select Type"
          />

          {/* Market */}
          <label htmlFor="Market">
            <span className="ncr-required">
              <span className="text-danger">* </span>Market:
            </span>
          </label>
          <ReactSelect
            id="Market"
            name="Market"
            value={fusionMarket.find(opt => opt.value === (formData.Market?.value || formData.Market)) || null}
            required
            onChange={(selectedOption) =>
              handleSelectChange(selectedOption, 'Market')
            }
            options={fusionMarket}
            placeholder="Select Market"
          />

          {/* Customer Complaint Number will be generated by Fusion Manage and shown in success notification */}

          {/* Customer Complaint Title */}
          <label htmlFor="NCRTitle">
            <span className="ncr-required">
              <span className="text-danger">* </span>Customer Complaint Title:
            </span>
          </label>
          <input type="text" id="NCRTitle" name="NCRTitle" value={formData.NCRTitle} onChange={handleChange} autoComplete="off" required />

          {/* Description */}
          <label htmlFor="Description">
            <span className="ncr-required">
              <span className="text-danger">* </span>Description:
            </span>
            <InfoTooltip message="What happened&#10;What should have happened&#10;What was the consequence" />
          </label>
          <textarea id="Description" name="Description" value={formData.Description} onChange={handleChange} autoComplete="off" style={{ resize: 'none' }} required rows="4"></textarea>

          {/* Incident Date */}
          <label htmlFor="IncidentDate">
            <span className="ncr-required">
              <span className="text-danger">* </span>Incident Date:
            </span>
          </label>
          <input type="date" id="IncidentDate" name="IncidentDate" value={formData.IncidentDate} onChange={handleChange} required />

          {/* Internal or External */}
          <label htmlFor="InternalOrExternal">
            <span className="ncr-required">
              <span className="text-danger">* </span>Internal or External:
            </span>
            <InfoTooltip message="Internal - Issue caused by an internal process failure&#10;Supplier - Issue caused by a supplied product or service&#10;External Audit - Issue raised by an external certifying body" />
          </label>
          <ReactSelect
            id="InternalOrExternal"
            name="InternalOrExternal"
            value={internalOrExternalOptions.find(opt => opt.value === (formData.InternalOrExternal?.value || formData.InternalOrExternal)) || null}
            required
            isLoading={loadingFusionData}
            onChange={(selectedOption) =>
              handleSelectChange(selectedOption, 'InternalOrExternal')
            }
            options={internalOrExternalOptions}
            placeholder="Select Internal or External"
          />

          {/* Related Supplier */}
          <label htmlFor="RelatedSupplier">
            Related Supplier:
          </label>
          <AsyncCreatable
            id="RelatedSupplier"
            name="RelatedSupplier"
            loadOptions={loadSupplierOptions}
            defaultOptions={true}
            isLoading={loadingFusionData}
            isCreatable={false}
            createOptionPosition="last"
            cacheOptions={false}
            value={(() => {
              if (!formData.RelatedSupplier) {
                return null;
              }
              
              // If it's a custom string (not an object), create an option for it
              if (typeof formData.RelatedSupplier === 'string') {
                return {
                  value: formData.RelatedSupplier,
                  label: formData.RelatedSupplier,
                  __isNew__: true
                };
              }
              
              // Find matching option by value
              if (typeof formData.RelatedSupplier === 'object' && !Array.isArray(formData.RelatedSupplier)) {
                // Check if it's already a valid option format
                if (formData.RelatedSupplier.value && formData.RelatedSupplier.label) {
                  return formData.RelatedSupplier;
                }
                
                // Try to find in supplierOptions
                const matchedOption = supplierOptions.find(opt => 
                  String(opt.value) === String(formData.RelatedSupplier.value || formData.RelatedSupplier)
                );
                return matchedOption || {
                  value: formData.RelatedSupplier.value || formData.RelatedSupplier,
                  label: formData.RelatedSupplier.label || formData.RelatedSupplier.value || formData.RelatedSupplier
                };
              }
              
              // Fallback for primitive values
              const matchedOption = supplierOptions.find(opt => 
                String(opt.value) === String(formData.RelatedSupplier)
              );
              return matchedOption || {
                value: String(formData.RelatedSupplier),
                label: String(formData.RelatedSupplier),
                __isNew__: true
              };
            })()}
            onChange={(selectedOption) => {
              console.log('[CustomerComplaintForm] RelatedSupplier onChange:', selectedOption);
              // Store the selected option (either from Fusion Manage picklist or Epicor)
              handleSelectChange(selectedOption, 'RelatedSupplier');
            }}
            placeholder="Search suppliers..."
            noOptionsMessage={({ inputValue }) => 
              inputValue ? `No suppliers found for "${inputValue}"` : 'Type to search suppliers...'
            }
            filterOption={null} // Disable client-side filtering since we're using server-side search
          />

          {/* Hours Associated */}
          <label htmlFor="CostOfNCR">
            <span className="ncr-required">
              <span className="text-danger">* </span>Hours Associated:
            </span>
            <InfoTooltip message="The number of hours that were spent troubleshooting and correcting the issue. This is any time spent over and above what would normally be required to carry out the job" />
          </label>
          <input type="number" id="CostOfNCR" name="CostOfNCR" value={formData.CostOfNCR} onChange={handleChange} autoComplete="off" required step="0.01" />

          {/* Project-related fields - only show if Project is selected */}
          {(formData.ProjectOrTechnology === 'Project' || formData.ProjectOrTechnology?.value === 'Project' || formData.ProjectOrTechnology?.label === 'Project') && (
            <>
              <h4>Project Information</h4>
              
              {/* Project Name */}
              <label htmlFor="ProjectName">
                <span className="ncr-required">
                  <span className="text-danger">* </span>Project Name:
                </span>
              </label>
              <ReactSelect
                id="ProjectName"
                name="ProjectName"
                value={selectedProjectNameOption}
                required
                isLoading={loadingProjects}
                onChange={(selectedOption) =>
                  handleSelectChange(selectedOption, 'ProjectName')
                }
                options={projectNameOptions}
                isDisabled={projectNameOptions.length === 0}
                placeholder={loadingProjects ? 'Loading projects from Epicor...' : (projectNameOptions.length === 0 ? 'No Epicor projects available' : 'Select a Project Name')}
                noOptionsMessage={() => 'No Epicor projects match your search'}
              />
              {!projectNameOptions.length && (
                <div className="text-muted small">No Epicor projects available.</div>
              )}

              {/* Job Number */}
              <label htmlFor="JobNumber">
                Job Number:
              </label>
              <ReactSelect
                id="JobNumber"
                name="JobNumber"
                value={jobOptions.find(opt => opt.value === (formData.JobNumber?.value || formData.JobNumber)) || null}
                isLoading={loadingJobs}
                onChange={(selectedOption) =>
                  handleSelectChange(selectedOption, 'JobNumber')
                }
                options={jobOptions}
                placeholder="Select a Job Number"
              />

              {/* Drawing Number */}
              <label htmlFor="DrawingNumber">
                Drawing Number:
                <InfoTooltip message="Drawing/Part number of affected component" />
              </label>
              <input type="text" id="DrawingNumber" name="DrawingNumber" value={formData.DrawingNumber} onChange={handleChange} autoComplete="off" />
            </>
          )}

          {/* Product-related fields - only show if Product is selected */}
          {(formData.ProjectOrTechnology === 'Product' || formData.ProjectOrTechnology?.value === 'Product' || formData.ProjectOrTechnology?.label === 'Product') && (
            <>
              <h4>Product Information</h4>
              
              {/* Product Name */}
              <label htmlFor="ProductName">
                <span className="ncr-required">
                  <span className="text-danger">* </span>Product Name:
                </span>
              </label>
              <ReactSelect
                key={`product-select-${productNameOptions.length}`}
                id="ProductName"
                name="ProductName"
                value={productNameOptions.find(opt => opt.value === (formData.ProductName?.value || formData.ProductName)) || null}
                required
                isLoading={loadingFusionData}
                onChange={(selectedOption) =>
                  handleSelectChange(selectedOption, 'ProductName')
                }
                options={productNameOptions}
                placeholder={loadingFusionData ? "Loading products from Fusion Manage..." : (productNameOptions.length === 0 ? "No products available" : "Select a Product Name")}
                noOptionsMessage={() => productNameOptions.length === 0 ? "No products available. Please check console for errors." : "No matching products"}
              />

              {/* Product Variant */}
              <label htmlFor="ProductVariant">
                Product Variant:
                <InfoTooltip message="if applicable" />
              </label>
              <input
                type="text"
                id="ProductVariant"
                name="ProductVariant"
                value={formData.ProductVariant || ''}
                onChange={handleChange}
                placeholder="Enter Product Variant"
                autoComplete="off"
              />

              {/* Software Variant */}
              <label htmlFor="SoftwareVersion">
                Software Variant:
              </label>
              <input type="text" id="SoftwareVersion" name="SoftwareVersion" value={formData.SoftwareVersion} onChange={handleChange} autoComplete="off" />

              {/* Product Serial Number */}
              <label htmlFor="ProductSerialNumber">
                Product Serial Number:
                <InfoTooltip message="If multiple serial numbers, please list them in the NCR description" />
              </label>
              <input type="text" id="ProductSerialNumber" name="ProductSerialNumber" value={formData.ProductSerialNumber} onChange={handleChange} autoComplete="off" />

              {/* Affected Quantity */}
              <label htmlFor="AffectedQuantity">
                <span className="ncr-required">
                  <span className="text-danger">* </span>Affected Quantity:
                </span>
              </label>
              <input type="number" id="AffectedQuantity" name="AffectedQuantity" value={formData.AffectedQuantity} onChange={handleChange} autoComplete="off" required />
            </>
          )}

          {/* Assignment fields */}
          <h4>Assigned To</h4>
          
          {/* Global Site Assigned - Hidden (auto-derived from site selection) */}
          <div style={{ display: 'none' }}>
            <label htmlFor="GlobalSiteAssigned">
              <span className="ncr-required">
                <span className="text-danger">* </span>Global Assigned Location:
              </span>
            </label>
            <ReactSelect
              id="GlobalSiteAssigned"
              name="GlobalSiteAssigned"
              value={globalSiteOptionsAssigned.find(opt => opt.value === (formData.GlobalSiteAssigned?.value || formData.GlobalSiteAssigned)) || null}
              required
              isLoading={!!(loadingFusionData || loadingGlobalSiteOptions)}
              onChange={(selectedOption) =>
                handleSelectChange(selectedOption, 'GlobalSiteAssigned')
              }
              options={globalSiteOptionsAssigned}
              placeholder={(loadingFusionData || loadingGlobalSiteOptions) ? "Loading sites..." : "Select Global Assigned Location"}
            />
          </div>

          {/* Site Assigned */}
          <label htmlFor="SiteAssigned">
            <span className="ncr-required">
              <span className="text-danger">* </span>Local Assigned Location:
            </span>
          </label>
          <ReactSelect
            id="SiteAssigned"
            name="SiteAssigned"
            value={siteOptionsAssigned.find(opt => opt.value === (formData.SiteAssigned?.value || formData.SiteAssigned)) || null}
            required
            isLoading={loadingFilteredOptions}
            isDisabled={false}
            onChange={(selectedOption) =>
              handleSelectChange(selectedOption, 'SiteAssigned')
            }
            options={siteOptionsAssigned}
            placeholder={loadingFilteredOptions ? "Loading sites..." : "Select Local Assigned Location"}
          />

          {/* Department Assigned */}
          <label htmlFor="DepartmentAssigned">
            <span className="ncr-required">
              <span className="text-danger">* </span>Department Assigned:
            </span>
          </label>
          <ReactSelect
            id="DepartmentAssigned"
            name="DepartmentAssigned"
            value={departmentOptionsAssigned.find(opt => opt.value === (formData.DepartmentAssigned?.value || formData.DepartmentAssigned)) || null}
            required
            isLoading={!!(loadingFilteredOptions && selectedGlobalSiteAssigned && selectedSiteAssigned)}
            isDisabled={!selectedGlobalSiteAssigned || !selectedSiteAssigned}
            onChange={(selectedOption) =>
              handleSelectChange(selectedOption, 'DepartmentAssigned')
            }
            options={departmentOptionsAssigned}
            placeholder={!selectedGlobalSiteAssigned || !selectedSiteAssigned ? "Select Global Site and Site first" : (loadingFilteredOptions ? "Loading valid departments..." : "Select Department Assigned")}
          />

          {/* Customer Complaint Champion Assigned */}
          <label htmlFor="NCRChampionAssigned">
            <span className="ncr-required">
              <span className="text-danger">* </span>Customer Complaint Champion Assigned:
            </span>
          </label>
          <ReactSelect
            id="NCRChampionAssigned"
            name="NCRChampionAssigned"
            value={ncrChampionOptions.find(opt => opt.value === (formData.NCRChampionAssigned?.value || formData.NCRChampionAssigned)) || null}
            required
            isDisabled={!selectedGlobalSiteAssigned || !selectedSiteAssigned || !formData.DepartmentAssigned}
            isLoading={!!(loadingFilteredOptions && selectedGlobalSiteAssigned && selectedSiteAssigned && formData.DepartmentAssigned)}
            onChange={(selectedOption) =>
              handleSelectChange(selectedOption, 'NCRChampionAssigned')
            }
            options={ncrChampionOptions}
            placeholder={(!selectedGlobalSiteAssigned || !selectedSiteAssigned || !formData.DepartmentAssigned) ? "Select Global Site, Site, and Department first" : (loadingFilteredOptions ? "Loading valid champions..." : "Select Customer Complaint Champion Assigned")}
          />

          {/* NCR Capability Assigned */}
          <label htmlFor="NCRCapabilityAssigned">
            <span className="ncr-required">
              <span className="text-danger">* </span>NCR Capability Assigned:
            </span>
          </label>
          <ReactSelect
            id="NCRCapabilityAssigned"
            name="NCRCapabilityAssigned"
            value={filteredCapabilityOptionsAssigned.find(opt => opt.value === (formData.NCRCapabilityAssigned?.value || formData.NCRCapabilityAssigned)) || null}
            required
            isDisabled={!selectedGlobalSiteAssigned || !selectedSiteAssigned || !formData.DepartmentAssigned}
            isLoading={!!(loadingFilteredOptions && selectedGlobalSiteAssigned && selectedSiteAssigned && formData.DepartmentAssigned)}
            onChange={(selectedOption) => {
              handleSelectChange(selectedOption, 'NCRCapabilityAssigned');
              // Auto-set QA Email when capability is selected
              if (selectedOption) {
                const matchingQAEmail = filteredQAEmailOptionsAssigned.find(opt => 
                  opt.value === selectedOption.value || opt.label === selectedOption.label
                );
                if (matchingQAEmail) {
                  handleSelectChange(matchingQAEmail, 'QAEmail');
                }
              }
            }}
            options={filteredCapabilityOptionsAssigned}
            placeholder={(!selectedGlobalSiteAssigned || !selectedSiteAssigned || !formData.DepartmentAssigned) ? "Select Global Site, Site, and Department first" : (loadingFilteredOptions ? "Loading valid capabilities..." : "Select NCR Capability Assigned")}
          />

          {/* Raised By Information */}
          <h4>Raised By</h4>
          
          {/* Global Site Raised By - Hidden (auto-derived from site selection) */}
          <div style={{ display: 'none' }}>
            <label htmlFor="GlobalSiteRaisedBy">
              <span className="ncr-required">
                <span className="text-danger">* </span>Global Location Raised By:
              </span>
            </label>
            <ReactSelect
              id="GlobalSiteRaisedBy"
              name="GlobalSiteRaisedBy"
              value={globalSiteOptionsRaisedBy.find(opt => opt.value === (formData.GlobalSiteRaisedBy?.value || formData.GlobalSiteRaisedBy)) || null}
              required
              isLoading={!!(loadingFusionData || loadingGlobalSiteOptions)}
              onChange={(selectedOption) =>
                handleSelectChange(selectedOption, 'GlobalSiteRaisedBy')
              }
              options={globalSiteOptionsRaisedBy}
              placeholder={(loadingFusionData || loadingGlobalSiteOptions) ? "Loading sites..." : "Select Global Location Raised By"}
            />
          </div>

          {/* Site Raised By */}
          <label htmlFor="SiteRaisedBy">
            <span className="ncr-required">
              <span className="text-danger">* </span>Local Site Raise By:
            </span>
          </label>
          <ReactSelect
            id="SiteRaisedBy"
            name="SiteRaisedBy"
            value={siteOptionsRaisedBy.find(opt => opt.value === (formData.SiteRaisedBy?.value || formData.SiteRaisedBy)) || null}
            required
            isLoading={loadingFilteredOptions}
            isDisabled={false}
            onChange={(selectedOption) =>
              handleSelectChange(selectedOption, 'SiteRaisedBy')
            }
            options={siteOptionsRaisedBy}
            placeholder={loadingFilteredOptions ? "Loading sites..." : "Select Local Site Raise By"}
          />

          {/* Department Raised By */}
          <label htmlFor="DepartmentRaisedBy">
            <span className="ncr-required">
              <span className="text-danger">* </span>Department Raised By:
            </span>
          </label>
          <ReactSelect
            id="DepartmentRaisedBy"
            name="DepartmentRaisedBy"
            value={departmentOptionsRaisedBy.find(opt => opt.value === (formData.DepartmentRaisedBy?.value || formData.DepartmentRaisedBy)) || null}
            required
            isLoading={!!(loadingFilteredOptions && selectedGlobalSiteRaisedBy)}
            isDisabled={!selectedGlobalSiteRaisedBy}
            onChange={(selectedOption) =>
              handleSelectChange(selectedOption, 'DepartmentRaisedBy')
            }
            options={departmentOptionsRaisedBy}
            placeholder={!selectedGlobalSiteRaisedBy ? "Select Global Site first" : (loadingFilteredOptions ? "Loading valid departments..." : "Select Department Raised By")}
          />

          {/* Raised By */}
          {/* Raised Date */}
          <label htmlFor="RaisedDate">
            <span className="ncr-required">
              <span className="text-danger">* </span>Raised Date:
            </span>
          </label>
          <input type="date" id="RaisedDate" name="RaisedDate" value={formData.RaisedDate} onChange={handleChange} required />

          {/* Raised By */}
          {/* Note: For workspace 736, SPAWNED_BY is read-only, so this field is optional */}
          <label htmlFor="RaisedBy">
            Raised By:
            <span className="text-muted small"> (Optional for Customer Complaints - auto-populated by system)</span>
          </label>
          <input type="text" id="RaisedBy" name="RaisedBy" value={formData.RaisedBy} onChange={handleChange} autoComplete="off" />

          <button
            type="button"
            className="btn btn-primary"
            onClick={handleSubmit}
            disabled={!canSubmit || isSubmitting}
          >
            {isSubmitting ? 'Submitting...' : 'Submit'}
          </button>
        </div>
    </div>
  );
}

export default CustomerComplaintForm;
